/**
 * Constants shared by the spaces-collection reports (objects, rentroll,
 * vacancies) and the shared filter modal — kept in the shared layer so no
 * report module depends on another report's folder.
 */

import { StatusMap } from '@/components/data-table/constants';
import { spacesField } from '../collections/spaces';
import type { TagUsagePeriod } from './spaces-filter-parts';
import type { ReportColumn, ReportGroupOption } from './types';

/**
 * `space.status` labels — the app-wide object StatusMap (the same 0–12
 * ContractStatus enum the backend persists on the space: the MIN of its
 * contracts' statuses, recalculated on contract changes;
 * `contracts/rentablecontracts/statusutils.py`).
 */
const OBJECT_STATUS_LABELS: Record<number, string> = Object.fromEntries(
  Object.entries(StatusMap.object).map(([value, status]) => [
    value,
    status.text,
  ])
);

/**
 * Objektstatus column shared by the rent roll and objects reports: the
 * backend-calculated space status, read straight from the synced
 * `space.status`. ⚠️ It reflects the CURRENT state at sync time — unlike
 * the Status column it is NOT re-derived at the report's as-of date.
 */
export const OBJECT_STATUS_COLUMN: ReportColumn = {
  key: 'object_status',
  header: 'Objektstatus',
  path: spacesField('space.status'),
  valueKind: 'enum',
  labelResolver: { kind: 'static_map', values: OBJECT_STATUS_LABELS },
  headerInfo:
    'Objektets status som systemet beräknar den (bästa avtalsstatus över ' +
    'objektets samtliga avtal). Visar NULÄGET — påverkas inte av ' +
    'rapportens valda datum, till skillnad från Status-kolumnen.',
};

/** Tag filter "Inkludera" choices, in display order. */
export const TAG_PERIOD_OPTIONS: { value: TagUsagePeriod; label: string }[] = [
  { value: 'active', label: 'Aktiva' },
  { value: 'past', label: 'Tidigare' },
  { value: 'upcoming', label: 'Kommande' },
];

export const COMMONAREA_TYPE_OPTION = {
  value: 'objects.commonarea',
  label: 'Gemensamma utrymmen',
} as const;

export const OBJECT_TYPE_OPTIONS = [
  { value: 'objects.apartment', label: 'Lägenheter' },
  { value: 'objects.industrialpremises', label: 'Lokaler' },
  { value: 'objects.parkingspot', label: 'Fordonsplatser' },
  { value: 'objects.outdoorsection', label: 'Utomhussektioner' },
  { value: 'objects.brfapartment', label: 'Bostadsrätter' },
] as const;

/**
 * Export grouping dimensions for the spaces reports (objects, rentroll,
 * vacancies). The relation names are present on every exported document,
 * so grouping is pure client-side partitioning.
 */
export const SPACES_GROUP_OPTIONS: ReportGroupOption[] = [
  {
    key: 'landlord',
    label: 'Bolag',
    column: {
      key: 'landlord_name',
      header: 'Bolag',
      path: spacesField('landlord.name'),
    },
  },
  {
    key: 'realestate',
    label: 'Fastighet',
    column: {
      key: 'realestate_name',
      header: 'Fastighet',
      path: spacesField('realestate.name'),
    },
  },
];
